import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import './AndroidAppDevelopment.css';

// Animation Variants
const animationVariants = {
  fadeIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 1, ease: 'easeInOut' },
  },
  slideUp: {
    initial: { y: 50, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    transition: { duration: 1, ease: 'easeInOut' },
  },
  scaleUp: {
    initial: { scale: 0 },
    animate: { scale: 1 },
    transition: { duration: 0.5, ease: 'easeOut' },
  },
  slideInFromLeft: {
    initial: { x: -100, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    transition: { duration: 1, ease: 'easeOut' },
  },
  bounce: {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    transition: { duration: 1, ease: 'easeOut', delay: 0.3 },
  },
  rotate: {
    initial: { rotate: 0 },
    animate: { rotate: 360 },
    transition: { duration: 2, repeat: Infinity, ease: 'linear' },
  },
};

const ServiceItem = ({ imgSrc, alt, title, description }) => (
  <motion.div className="unique-service-item" {...animationVariants.bounce}>
    <motion.div {...animationVariants.scaleUp}>
      <img src={imgSrc} alt={alt} className="unique-service-icon" />
    </motion.div>
    <motion.h3 {...animationVariants.slideUp} className="unique-service-title">{title}</motion.h3>
    <motion.p {...animationVariants.fadeIn} className="unique-service-description">{description}</motion.p>
  </motion.div>
);

const AndroidAppDevelopment = () => {
  return (
    <div className="unique-android-app-development-container">
      {/* Background Parallax Section */}
      <header className="unique-page-header">
        <div className="unique-video-background">
          <motion.video
            autoPlay
            loop
            muted
            className="unique-video-background-element"
            initial={{ scale: 1.1 }}
            animate={{ scale: 1.05 }}
            transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
          >
            <source src="/videos/6.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </motion.video>
        </div>
        <div className="unique-overlay">
          <motion.h1 {...animationVariants.fadeIn} className="unique-title">Android App Development</motion.h1>
          <motion.p {...animationVariants.slideUp} className="unique-subtitle">
            Transforming ideas into native Android and Flutter cross-platform applications with cutting-edge technology and seamless user experiences.
          </motion.p>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="unique-header-button"
          >
          </motion.button>
        </div>
      </header>

      {/* Services Section */}
      <section className="unique-services-section">
        <ServiceItem
          imgSrc="/images/android/3.png"
          alt="Mobile Design"
          title="Mobile Design"
          description="Creating intuitive and visually stunning UI/UX designs tailored for Android applications."
        />
        <ServiceItem
          imgSrc="/images/android/2.jpg"
          alt="Development"
          title="Development"
          description="Expertise in native Android development using Kotlin and Java, and cross-platform development with Flutter."
        />
        <ServiceItem
          imgSrc="/images/android/1.jpg"
          alt="Testing"
          title="Testing"
          description="Ensuring high-quality apps through rigorous testing using tools like JUnit, Espresso, and Flutter Test Performance."
        />
        <ServiceItem
          imgSrc="/images/android/98.webp"
          alt="Play Store Hosting"
          title="Play Store Hosting"
          description="Assisting in deploying your application to the Google Play Store, ensuring compliance with all guidelines."
        />
      </section>

      {/* Roadmap Section with Animation */}
      <section className="unique-roadmap-section">
        <motion.h2 {...animationVariants.slideUp} className="unique-roadmap-title">Development Roadmap</motion.h2>
        <motion.ol {...animationVariants.fadeIn} className="unique-roadmap-list">
          <li>Requirement Analysis & Planning</li>
          <li>UI/UX Design</li>
          <li>Development (Native/Flutter)</li>
          <li>Integration of APIs and Third-party Services</li>
          <li>Testing & Quality Assurance</li>
          <li>Deployment on Play Store</li>
          <li>Post-launch Support & Updates</li>
        </motion.ol>
      </section>

      {/* Why Choose Us Section */}
      <section className="unique-why-choose-us">
        <motion.h2 {...animationVariants.slideUp} className="unique-why-choose-us-title">Why Choose Us?</motion.h2>
        <motion.p {...animationVariants.fadeIn} className="unique-why-choose-us-description">
          Our team is committed to building cutting-edge Android apps that provide top-notch performance, security, and user experience.
        </motion.p>
        <motion.ul {...animationVariants.fadeIn} className="unique-why-choose-us-list">
          <li><i className="fas fa-users"></i> Expert Team with Industry Experience</li>
          <li><i className="fas fa-dollar-sign"></i> Cost-effective Solutions</li>
          <li><i className="fas fa-cogs"></i> Customizable Features</li>
          <li><i className="fas fa-clock"></i> On-time Delivery</li>
        </motion.ul>
      </section>

      {/* CTA Section with Strong Hover Effects */}
      <section className="cta-section">
        <motion.div 
          {...animationVariants.container} 
          className="cta-content-wrapper"
        >
          <motion.h2 
            {...animationVariants.slideUp} 
            className="cta-title"
          >
            Ready to Build Your Android App?
          </motion.h2>
          <motion.p 
            {...animationVariants.fadeIn} 
            className="cta-description"
          >
            Let’s collaborate to turn your vision into a fully functional and successful Android application.
          </motion.p>
          <Link to="/ContactPage" className="cta-link">
            <motion.button
              whileHover={{ scale: 1.05, rotate: 2 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="cta-button"
            >
              Get In Touch
            </motion.button>
          </Link>
        </motion.div>
      </section>
    </div>
  );
};

export default AndroidAppDevelopment;
